import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ModalShell } from '../../components/ui/ModalShell';
import {
  apiErrorMessage,
  billingService,
  formatDay,
  yuan,
  type CreatedOrder,
  type PayIntent,
  type PayMethod,
  type Pricing,
  type Quote,
} from './billingService';

interface PayDialogProps {
  teamId: string;
  intent: PayIntent;
  /** 买席位时的默认数量（一般是现在还差几个） */
  defaultSeats: number;
  pricing: Pricing;
  /** 后端是模拟支付模式（只在开发/测试环境）：显示「模拟已支付」按钮 */
  mock: boolean;
  onClose: () => void;
  /** 支付成功、席位已到账 */
  onPaid: () => void;
}

const METHODS: { value: PayMethod; label: string }[] = [
  { value: 'ALIPAY', label: '支付宝' },
  { value: 'WECHAT', label: '微信支付' },
];

export const PayDialog: React.FC<PayDialogProps> = ({ teamId, intent, defaultSeats, pricing, mock, onClose, onPaid }) => {
  const [step, setStep] = useState<'choose' | 'pay' | 'done'>('choose');
  const [seats, setSeats] = useState(Math.max(1, defaultSeats));
  const [method, setMethod] = useState<PayMethod>('ALIPAY');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState<CreatedOrder | null>(null);
  const [expired, setExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const paidRef = useRef(false);

  // 选席位数 → 实时报价（金额由服务端算）
  useEffect(() => {
    if (step !== 'choose') return;
    let cancelled = false;
    setQuote(null);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const q = await billingService.quote(teamId, intent, intent === 'RENEW' ? undefined : seats);
        if (!cancelled) setQuote(q);
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e, '报价失败'));
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [step, teamId, intent, seats]);

  const handlePay = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await billingService.createOrder(teamId, intent, intent === 'RENEW' ? undefined : seats, method);
      setOrder(created);
      setExpired(false);
      paidRef.current = false;
      setStep('pay');
    } catch (e) {
      setError(apiErrorMessage(e, '下单失败'));
    } finally {
      setBusy(false);
    }
  };

  // 出二维码后：每 2.5 秒问一次订单状态，并倒计时
  useEffect(() => {
    if (step !== 'pay' || !order) return;
    const tick = () => setSecondsLeft(Math.max(0, Math.round((new Date(order.expireAt).getTime() - Date.now()) / 1000)));
    tick();
    const clock = setInterval(tick, 1000);
    const poll = setInterval(async () => {
      try {
        const s = await billingService.getOrder(order.orderId);
        if (s.status === 'PAID' && !paidRef.current) {
          paidRef.current = true;
          setStep('done');
          onPaid();
        } else if (s.status === 'CLOSED') {
          setExpired(true);
        }
      } catch {
        /* 偶发网络错误，下一轮再问 */
      }
    }, 2500);
    return () => { clearInterval(clock); clearInterval(poll); };
  }, [step, order, onPaid]);

  const handleMockPay = async () => {
    if (!order) return;
    setBusy(true);
    try {
      await billingService.mockPay(order.orderId);
    } catch (e) {
      setError(apiErrorMessage(e, '模拟支付失败'));
    } finally {
      setBusy(false);
    }
  };

  const title = intent === 'RENEW' ? '续费席位' : '购买席位';
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <ModalShell title={title} size="md" onClose={onClose} closeOnOverlay={step !== 'pay'}>
      <div className="px-8 pb-8">
        {step === 'choose' && (
          <>
            {intent === 'BUY_SEATS' && (
              <div className="mb-6">
                <label className="text-xs text-gray-500 block mb-2">购买席位数（每个席位 {yuan(pricing.pricePerSeatCents)} / 年）</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="w-9 h-9 rounded-md border border-gray-300 text-lg hover:bg-gray-50 disabled:opacity-40"
                    onClick={() => setSeats(s => Math.max(1, s - 1))}
                    disabled={seats <= 1}
                    aria-label="减少"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={seats}
                    onChange={(e) => setSeats(Math.min(500, Math.max(1, Math.floor(Number(e.target.value) || 1))))}
                    className="w-20 text-center text-lg rounded-md border border-gray-200 px-2 py-1.5 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  />
                  <button
                    type="button"
                    className="w-9 h-9 rounded-md border border-gray-300 text-lg hover:bg-gray-50"
                    onClick={() => setSeats(s => Math.min(500, s + 1))}
                    aria-label="增加"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            <div className="mb-6 rounded-lg bg-gray-50 px-4 py-3 text-sm">
              {quote ? (
                <>
                  <div className="flex items-baseline justify-between">
                    <span className="text-gray-600">{quote.description}</span>
                    <span className="text-2xl font-semibold text-gray-900">{yuan(quote.amountCents)}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    付费席位共 {quote.newPaidSeats} 个，有效期到 {formatDay(quote.newPaidUntil)}
                    {quote.kind === 'ADD' && `；按剩余 ${quote.billedDays} 天折算，与团队同一天到期`}
                  </div>
                </>
              ) : (
                <span className="text-gray-400">{error ? '' : '计算中…'}</span>
              )}
            </div>

            <div className="mb-6">
              <label className="text-xs text-gray-500 block mb-2">支付方式</label>
              <div className="flex gap-2">
                {METHODS.map(m => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMethod(m.value)}
                    className={`flex-1 text-sm px-4 py-2.5 rounded-md border transition-colors ${
                      method === m.value ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

            <button
              type="button"
              onClick={handlePay}
              disabled={busy || !quote}
              className="w-full text-sm px-4 py-2.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500"
            >
              {busy ? '正在生成二维码…' : quote ? `去支付 ${yuan(quote.amountCents)}` : '去支付'}
            </button>
            <div className="mt-3 text-xs text-gray-400 text-center">agent 成员不占席位，不收费</div>
          </>
        )}

        {step === 'pay' && order && (
          <div className="flex flex-col items-center">
            <div className="text-3xl font-semibold text-gray-900">{yuan(order.amountCents)}</div>
            <div className="mt-1 text-sm text-gray-500">{order.quote.description}</div>

            <div className="relative mt-6 p-4 rounded-lg border border-gray-200 bg-white">
              <QRCodeSVG value={order.qrCode} size={208} level="M" />
              {expired && (
                <div className="absolute inset-0 rounded-lg bg-white/90 flex flex-col items-center justify-center gap-3">
                  <div className="text-sm text-gray-600">二维码已过期</div>
                  <button type="button" onClick={() => { setStep('choose'); setOrder(null); }} className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700">
                    重新下单
                  </button>
                </div>
              )}
            </div>

            <div className="mt-4 text-sm text-gray-600">
              用{order.method === 'ALIPAY' ? '支付宝' : '微信'}扫码支付
              {!expired && <span className="ml-2 text-gray-400">剩余 {mm}:{ss}</span>}
            </div>
            <div className="mt-1 text-xs text-gray-400">付款后自动到账，无需手动刷新</div>

            {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

            {mock && !expired && (
              <button
                type="button"
                onClick={handleMockPay}
                disabled={busy}
                className="mt-6 text-sm px-4 py-2 rounded-md border border-dashed border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
              >
                模拟已支付（仅开发环境）
              </button>
            )}

            <button type="button" onClick={onClose} className="mt-4 text-sm text-gray-400 hover:text-gray-600">
              稍后再付，关闭
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="flex flex-col items-center py-6">
            <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-2xl">✓</div>
            <div className="mt-4 text-lg font-semibold text-gray-900">已到账</div>
            <div className="mt-1 text-sm text-gray-500">席位已生效，新成员可以加入了</div>
            <button type="button" onClick={onClose} className="mt-6 text-sm px-5 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700">
              完成
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
};
